import React from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Button,
  Container,
  Drawer,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  CircularProgress,
  Box,
} from "@mui/material";
import { Typography } from "@mui/material";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DoughnutChart from "../../components/charts/DoughnutChart";
import { myDesignService } from "../../services";
import { ArrowBack, ArrowDropDown, List, PieChart } from "@mui/icons-material";
// import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
// import { addYears, endOfYear, startOfYear } from "date-fns";
// import DateFnsUtils from "@date-io/date-fns";
// import { formatForRequest, locales } from "../../helpers/format";
import { useViewport } from "../../helpers";

import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import i18next from "i18next";
import GlobalStats from "../globalStats/GlobalStats";

const PREFIX = "GlobalStatistics";

const classes = {
  root: `${PREFIX}-root`,
  filters: `${PREFIX}-filters`,
  filterBy: `${PREFIX}-filterBy`,
  filterDates: `${PREFIX}-filterDates`,
  pieChart: `${PREFIX}-pieChart`,
  groupBy: `${PREFIX}-groupBy`,
  groupByMobile: `${PREFIX}-groupByMobile`,
  paddingTitle: `${PREFIX}-paddingTitle`,
  groupByDrawer: `${PREFIX}-groupByDrawer`,
  back: `${PREFIX}-back`,
  groupSelected: `${PREFIX}-groupSelected`,
  titleGroup: `${PREFIX}-titleGroup`,
  titleGraph: `${PREFIX}-titleGraph`,
  inlineBlock: `${PREFIX}-inlineBlock`,
  titleStats: `${PREFIX}-titleStats`,
  toggleButtonHeight: `${PREFIX}-toggleButtonHeight`,
  loaderContainer: `${PREFIX}-loaderContainer`,
  doughnutContainer: `${PREFIX}-doughnutContainer`,
  dataGridContainer: `${PREFIX}-dataGridContainer`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`&.${classes.root}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.filters}`]: {
    width: "100%",
    minHeight: "50px",
    padding: "0.5rem",
    marginBottom: "0.2rem",
    backgroundColor: "#333",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
  },

  [`& .${classes.filterBy}`]: {
    flex: 1,
    textAlign: "center",
  },

  [`& .${classes.filterDates}`]: {
    display: "flex",
    flex: 4,
    justifyContent: "space-evenly",
    flexWrap: "wrap",
  },

  [`& .${classes.pieChart}`]: {
    position: "relative",
    width: "100%",
    maxWidth: "960px",
    height: "calc(100vh - 300px)",
    minHeight: "450px",
    backgroundColor: "#323232",
  },

  [`& .${classes.groupBy}`]: {
    position: "absolute",
    top: "1rem",
    right: 0,
    zIndex: 999,
  },

  [`& .${classes.groupByMobile}`]: {
    position: "absolute",
    top: "-5px",
    right: 0,
    zIndex: 999,
  },

  [`& .${classes.paddingTitle}`]: {
    paddingTop: "12px",
  },

  [`& .${classes.groupByDrawer}`]: {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    paddingLeft: "24px",
    paddingBottom: "12px",
  },

  [`& .${classes.back}`]: {
    position: "absolute",
    left: "5px",
    top: "5px",
    zIndex: 999,
  },

  [`& .${classes.groupSelected}`]: {
    display: "block",
    textAlign: "center",
  },

  [`& .${classes.titleGroup}`]: {
    display: "inline-block",
  },

  [`& .${classes.titleGraph}`]: {
    position: "absolute",
    bottom: "75px",
    left: "50%",
    transform: "translateX(-50%)",
    color: "white",
    zIndex: 999,
    display: "flex",
  },

  [`& .${classes.inlineBlock}`]: {
    display: "inline-block",
  },

  [`& .${classes.titleStats}`]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  [`& .${classes.toggleButtonHeight}`]: {
    height: 50,
  },

  [`& .${classes.loaderContainer}`]: {
    margin: "0 auto",
    textAlign: "center",
  },

  [`& .${classes.doughnutContainer}`]: {
    width: "100%",
    height: "90%",
    padding: "1rem 0",
  },

  [`& .${classes.dataGridContainer}`]: {
    height: 600,
    width: "100%",
    backgroundColor: "#323232",
  },
}));

const columns = [
  { field: "id", headerName: "ID", width: 70, type: "number" },
  { field: "frameLabel", headerName: "MyDesign", width: 150 },
  { field: "target", headerName: "Cible", width: 150 },
  { field: "index", headerName: "Idx", type: "number", width: 120 },
  { field: "redirectCount", headerName: "Nb", type: "number", width: 120 },
];

export default function GlobalStatistics(props) {
  const { width } = useViewport();
  const { t } = useTranslation();
  const [dataFormatted, setDataFormatted] = React.useState([]);
  const [frameDataFormatted, setFrameDataFormatted] = React.useState([]);
  const [targetDataFormatted, setTargetDataFormatted] = React.useState([]);
  const [zoneDataFormatted, setZoneDataFormatted] = React.useState([]);
  const [frameSelected, setFrameSelected] = React.useState(null);
  const [targetSelected, setTargetSelected] = React.useState(null);
  const [level, setLevel] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [group, setGroup] = React.useState(t("stats.myDesigns"));
  const [isLoading, setIsLoading] = React.useState(false);
  const [filters, setFilters] = React.useState({
    // beginDate: startOfYear(Date.now()),
    // endDate: endOfYear(Date.now()),
    beginDate: dayjs().startOf("year").format("YYYY-MM-DDTHH:mm:ssZ"),
    endDate: dayjs().endOf("year").format("YYYY-MM-DDTHH:mm:ssZ"),
  });
  const [open, setOpen] = React.useState(false);
  const [display, setDisplay] = React.useState("pie");
  const [allData, setAllData] = React.useState([]);
  const [isExp, setIsExp] = React.useState(false);

  function handleDisplay(event, newDisplay) {
    setDisplay(newDisplay);
  }

  React.useMemo(() => {
    getData();
  }, []);

  React.useMemo(() => {
    searchData();
  }, [filters]);

  function searchData() {
    myDesignService
      .fetchGlobalStats(
        dayjs(filters.beginDate).format("YYYY-MM-DDTHH:mm:ssZ"),
        dayjs(filters.endDate).format("YYYY-MM-DDTHH:mm:ssZ")
      )
      .then((allData) => {
        const zeTotal = allData.reduce(
          (accumulator, currentValue) =>
            accumulator + currentValue.redirectCount,
          0
        );
        formatData(allData, zeTotal);
        setIsLoading(false);
      });
  }

  function getData() {
    setIsLoading(true);
    myDesignService
      .fetchGlobalStats(
        dayjs(filters.beginDate).format("YYYY-MM-DDTHH:mm:ssZ"),
        dayjs(filters.endDate).format("YYYY-MM-DDTHH:mm:ssZ")
      )
      .then((allData) => {
        const zeTotal = allData.reduce(
          (accumulator, currentValue) =>
            accumulator + currentValue.redirectCount,
          0
        );
        if (zeTotal <= 0) {
          // setFilters({ ...filters, beginDate: addYears(filters.beginDate, -1) });
          setFilters({
            ...filters,
            beginDate: dayjs(filters.beginDate)
              .add(-1, "year")
              .format("YYYY-MM-DDTHH:mm:ssZ"),
          });
        } else formatData(allData, zeTotal);
      });
  }

  function formatData(allData, zeTotal) {
    setAllData(
      allData.map((d, index) => {
        return { ...d, id: index };
      })
    );
    let zeData = allData;
    if (props.optimize) {
      zeData = allData.filter((data) => data.redirectCount > 0);
    }
    const result = zeData.reduce((accumulator, currentValue) => {
      if (Object.keys(accumulator).indexOf(currentValue.frameLabel) >= 0) {
        accumulator[currentValue.frameLabel] = {
          ...accumulator[currentValue.frameLabel],
          value:
            accumulator[currentValue.frameLabel].value +
            currentValue.redirectCount,
        };
      } else {
        accumulator[currentValue.frameLabel] = {
          id: currentValue.frameLabel,
          value: currentValue.redirectCount,
          label: currentValue.frameLabel,
          frameId: currentValue.frameId,
        };
      }
      return accumulator;
    }, []);

    const resultTarget = zeData.reduce((accumulator, currentValue) => {
      if (Object.keys(accumulator).indexOf(currentValue.target) >= 0) {
        accumulator[currentValue.target] = {
          ...accumulator[currentValue.target],
          value:
            accumulator[currentValue.target].value + currentValue.redirectCount,
        };
      } else {
        accumulator[currentValue.target] = {
          id: currentValue.target,
          value: currentValue.redirectCount,
          label: currentValue.target,
          frameId: currentValue.frameId,
          target: currentValue.target,
        };
      }
      return accumulator;
    }, []);

    const resultZone = zeData.reduce((accumulator, currentValue) => {
      if (Object.keys(accumulator).indexOf(getKey(currentValue)) >= 0) {
        accumulator[getKey(currentValue)] = {
          ...accumulator[getKey(currentValue)],
          value:
            accumulator[getKey(currentValue)].value +
            currentValue.redirectCount,
        };
      } else {
        accumulator[getKey(currentValue)] = {
          id: `${currentValue.target}-${currentValue.label}`,
          value: currentValue.redirectCount,
          label: currentValue.label,
          frameId: currentValue.frameId,
          target: currentValue.target,
        };
      }
      return accumulator;
    }, []);
    setZoneDataFormatted(Object.values(resultZone));
    setTargetDataFormatted(Object.values(resultTarget));
    setFrameDataFormatted(Object.values(result));
    setTotal(zeTotal);
    setDataFormatted(Object.values(result));
  }

  function getKey(value) {
    return `${value.frameId}-${value.target}-${value.index}`;
  }

  function zoom(a) {
    if (width <= 768) return;
    if (group !== t("stats.zones")) {
      let filteredData;
      switch (level) {
        case 0:
          if (group === t("stats.targets")) {
            filteredData = zoneDataFormatted.filter(
              (d) => d.frameId === a.data.frameId && d.target === a.data.target
            );
            setLevel((prev) => prev + 2);
            setTargetSelected(a.data);
          } else {
            filteredData = targetDataFormatted.filter(
              (d) => d.frameId === a.data.frameId
            );
            setLevel((prev) => prev + 1);
            setFrameSelected(a.data);
          }
          setTotal(
            filteredData.reduce(
              (accumulator, currentValue) => accumulator + currentValue.value,
              0
            )
          );
          setDataFormatted(filteredData);
          break;
        case 1:
          filteredData = zoneDataFormatted.filter(
            (d) => d.frameId === a.data.frameId && d.target === a.data.target
          );
          setTotal(
            filteredData.reduce(
              (accumulator, currentValue) => accumulator + currentValue.value,
              0
            )
          );
          setDataFormatted(filteredData);
          setLevel((prev) => prev + 1);
          setTargetSelected(a.data);
          break;
        default:
          break;
      }
    }
  }

  function back() {
    switch (level) {
      case 2:
        if (group === t("stats.targets")) {
          setLevel(0);
          showGroupView(group);
        } else {
          setTargetSelected(null);
          const filteredData = targetDataFormatted.filter(
            (d) => d.frameId === frameSelected.frameId
          );
          setTotal(
            filteredData.reduce(
              (accumulator, currentValue) => accumulator + currentValue.value,
              0
            )
          );
          setDataFormatted(filteredData);
          setLevel((prev) => prev - 1);
        }
        break;
      case 1:
        showGroupView(group);
        setLevel((prev) => prev - 1);
        break;
      default:
        break;
    }
  }

  function handleDateChange(name, value) {
    if (value) {
      setFilters({
        ...filters,
        [name]: dayjs(value).format("YYYY-MM-DDTHH:mm:ssZ"),
      });
    }
  }

  function changeGroup(e) {
    setGroup(e.target.value);
    setLevel(0);
    showGroupView(e.target.value);
    setOpen(false);
  }

  function showGroupView(group) {
    setTotal(
      frameDataFormatted.reduce(
        (accumulator, currentValue) => accumulator + currentValue.value,
        0
      )
    );
    setFrameSelected(null);
    setTargetSelected(null);
    switch (group) {
      case t("stats.myDesigns"):
        setDataFormatted(frameDataFormatted);
        break;
      case t("stats.targets"):
        setDataFormatted(targetDataFormatted);
        break;
      case t("stats.zones"):
        setDataFormatted(zoneDataFormatted);
        break;
      default:
        setDataFormatted(frameDataFormatted);
        break;
    }
  }

  function toggleDrawer() {
    setOpen((prev) => !prev);
  }

  const MyDrawer = React.memo(function MyDrawer(props) {
    return (
      <StyledDiv>
        <div className={classes.groupByMobile}>
          <h4 className={classes.titleGroup}>{t("stats.groupBy")}</h4>
          <IconButton onClick={props.toggleDrawer} size="large">
            <ArrowDropDown />
          </IconButton>
          <span className={classes.groupSelected}>{group}</span>
          <Drawer
            anchor="bottom"
            open={props.open}
            onClose={props.toggleDrawer}
          >
            <FormControl component="fieldset" className={classes.groupByDrawer}>
              <FormLabel component="legend" className={classes.paddingTitle}>
                {t("stats.groupBy")}
              </FormLabel>
              <RadioGroup
                aria-label="group"
                name="group"
                value={group}
                onChange={changeGroup}
              >
                <FormControlLabel
                  value={t("stats.myDesigns")}
                  control={<Radio size="small" color="primary" />}
                  label={t("stats.myDesigns")}
                />
                <FormControlLabel
                  value={t("stats.targets")}
                  control={<Radio size="small" color="primary" />}
                  label={t("stats.targets")}
                />
                <FormControlLabel
                  value={t("stats.zones")}
                  control={<Radio size="small" color="primary" />}
                  label={t("stats.zones")}
                />
              </RadioGroup>
            </FormControl>
          </Drawer>
        </div>
      </StyledDiv>
    );
  });

  return (
    <StyledDiv>
      <Container maxWidth="md" className={classes.root}>
        {isLoading ? (
          <div className={classes.loaderContainer}>
            <CircularProgress size={50} />
            <Typography>{t("loaders.fetching")}</Typography>
          </div>
        ) : (
          <React.Fragment>
            <div className={classes.titleStats}>
              <h2 className={classes.inlineBlock}>{t("stats.title")}</h2>

              {!isExp ? (
                <ToggleButtonGroup
                  value={display}
                  exclusive
                  onChange={handleDisplay}
                  aria-label="text alignment"
                  className={classes.toggleButtonHeight}
                >
                  <ToggleButton value="pie" aria-label="pie chart">
                    <PieChart />
                  </ToggleButton>
                  <ToggleButton value="list" aria-label="list">
                    <List />
                  </ToggleButton>
                </ToggleButtonGroup>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setIsExp(false)}
                >
                  {t("stats.swapToMyDesignButton")}
                </Button>
              )}
            </div>
            {!isExp && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "1rem",
                }}
              >
                <Button
                  onClick={() => setIsExp(true)}
                  variant="contained"
                  color="primary"
                  size="medium"
                  sx={{ margin: "0 auto" }}
                >
                  {t("stats.swapToExpButton")}
                </Button>
              </Box>
            )}
            {isExp ? (
              <GlobalStats />
            ) : (
              <React.Fragment>
                <Paper elevation={3} className={classes.filters}>
                  <h3 className={classes.filterBy}>{t("stats.filterBy")}</h3>
                  <div className={classes.filterDates}>
                    {/* <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locales[i18n.languages[1]]}> */}
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale={i18next.language.substring(0, 2)}
                    >
                      <div>
                        {/* <KeyboardDatePicker
                      margin="normal"
                      id="from-date-picker-dialog"
                      label={t("stats.from")}
                      format="P"
                      value={filters.beginDate}
                      onChange={(e) => handleDateChange('beginDate', e)}
                      KeyboardButtonProps={{
                        'aria-label': 'change date',
                      }}
                      maxDate={filters.endDate}
                    /> */}
                        <DatePicker
                          margin="normal"
                          id="from-date-picker-dialog"
                          label={t("stats.from")}
                          value={dayjs(filters.beginDate)}
                          onChange={(e) => handleDateChange("beginDate", e)}
                          maxDate={dayjs(filters.endDate)}
                        />
                      </div>
                      <div>
                        {/* <KeyboardDatePicker
                      margin="normal"
                      id="to-date-picker-dialog"
                      label={t("stats.to")}
                      format="P"
                      value={filters.endDate}
                      onChange={(e) => handleDateChange('endDate', e)}
                      KeyboardButtonProps={{
                        'aria-label': 'change date',
                      }}
                      minDate={filters.beginDate}
                    /> */}
                        <DatePicker
                          margin="normal"
                          id="to-date-picker-dialog"
                          label={t("stats.to")}
                          value={dayjs(filters.endDate)}
                          onChange={(e) => handleDateChange("endDate", e)}
                          minDate={dayjs(filters.beginDate)}
                        />
                      </div>
                      {/* </MuiPickersUtilsProvider> */}
                    </LocalizationProvider>
                  </div>
                </Paper>
                <Paper
                  elevation={3}
                  className={display === "pie" ? classes.pieChart : ""}
                >
                  {display === "pie" && (
                    <div>
                      {width <= 768 ? (
                        <React.Fragment>
                          <MyDrawer open={open} toggleDrawer={toggleDrawer} />
                        </React.Fragment>
                      ) : (
                        <FormControl
                          component="fieldset"
                          className={classes.groupBy}
                        >
                          <FormLabel component="legend">
                            {t("stats.groupBy")}
                          </FormLabel>
                          <RadioGroup
                            aria-label="group"
                            name="group"
                            value={group}
                            onChange={changeGroup}
                          >
                            <FormControlLabel
                              value={t("stats.myDesigns")}
                              control={<Radio size="small" color="primary" />}
                              label={t("stats.myDesigns")}
                            />
                            <FormControlLabel
                              value={t("stats.targets")}
                              control={<Radio size="small" color="primary" />}
                              label={t("stats.targets")}
                            />
                            <FormControlLabel
                              value={t("stats.zones")}
                              control={<Radio size="small" color="primary" />}
                              label={t("stats.zones")}
                            />
                          </RadioGroup>
                        </FormControl>
                      )}
                    </div>
                  )}
                  {level !== 0 && (
                    <Button onClick={back} className={classes.back}>
                      <ArrowBack />
                      {level === 1 && <span>{t("stats.globalView")}</span>}
                      {level === 2 && group === t("stats.myDesigns") && (
                        <span>{frameSelected.label}</span>
                      )}
                      {level === 2 && group !== t("stats.myDesigns") && (
                        <span>{t("stats.globalView")}</span>
                      )}
                    </Button>
                  )}
                  {dataFormatted && display === "pie" && (
                    <div className={classes.doughnutContainer}>
                      <DoughnutChart data={dataFormatted} zoom={zoom} />
                      <div className={classes.titleGraph}>
                        {frameSelected && <h3>{frameSelected.label}</h3>}
                        {frameSelected && targetSelected && (
                          <h3>&nbsp;-&nbsp;</h3>
                        )}
                        {targetSelected && <h3>{targetSelected.label}</h3>}
                      </div>
                    </div>
                  )}
                  {dataFormatted && display === "list" && (
                    <div className={classes.dataGridContainer}>
                      <DataGrid
                        rows={allData}
                        columns={columns}
                        initialState={{
                          pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        pageSizeOptions={[5, 10, 25, 50]}
                      />
                    </div>
                  )}
                </Paper>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </Container>
    </StyledDiv>
  );
}
